import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import JobModal from './JobModal';
import InvoiceModal from './InvoiceModal';
import JobCompletedDialog from './JobCompletedDialog';
import { jobService } from '../services/jobService';
import { invoiceService } from '../services/invoiceService';
import { jobNoteService } from '../services/jobNoteService';
import { jobAuditService } from '../services/jobAuditService';
import JourneyModal from './JourneyModal';
import { journeyService } from '../services/journeyService';
import EstimateModal from './EstimateModal';
import { estimateService } from '../services/estimateService';
import QuoteModal from './QuoteModal';
import { quoteService } from '../services/quoteService';
import ExpenseModal from './ExpenseModal';
import { expenseService } from '../services/expenseService';
import { taskService } from '../services/taskService';
import { taskSetService } from '../services/taskSetService';
import { documentService } from '../services/documentService';

const ViewJob = () => {
  const { id } = useParams();
  const { user, logout } = useAuth();

  const [job, setJob] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [noteDeleteConfirm, setNoteDeleteConfirm] = useState(null);
  const [noteEditId, setNoteEditId] = useState(null);
  const [noteEditText, setNoteEditText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [completedJobDialog, setCompletedJobDialog] = useState(null);
  const [activeFinanceTab, setActiveFinanceTab] = useState('estimates');
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceDeleteConfirm, setInvoiceDeleteConfirm] = useState(false);
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);
  const [payForm, setPayForm] = useState({ date_paid: '', amount_paid: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [showAddJourneyModal, setShowAddJourneyModal] = useState(false);
  const [editingJourney, setEditingJourney] = useState(null);
  const [journeyDeleteConfirm, setJourneyDeleteConfirm] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [showAddEstimateModal, setShowAddEstimateModal] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [estimateDeleteConfirm, setEstimateDeleteConfirm] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [showAddQuoteModal, setShowAddQuoteModal] = useState(false);
  const [jobExpenses, setJobExpenses] = useState([]);
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseDeleteConfirm, setExpenseDeleteConfirm] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [quoteDeleteConfirm, setQuoteDeleteConfirm] = useState(null);
  const [jobTasks, setJobTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [taskBulkLoading, setTaskBulkLoading] = useState(false);
  const [taskCompleteConfirmPending, setTaskCompleteConfirmPending] = useState(false);
  const [taskDeleteConfirmPending, setTaskDeleteConfirmPending] = useState(false);
  const [taskSets, setTaskSets] = useState([]);
  const [showApplyTaskSetModal, setShowApplyTaskSetModal] = useState(false);
  const [selectedTaskSetId, setSelectedTaskSetId] = useState('');
  const [applyingTaskSet, setApplyingTaskSet] = useState(false);
  const [jobDocuments, setJobDocuments] = useState([]);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [uploadDocFiles, setUploadDocFiles] = useState([]);
  const [uploadDocError, setUploadDocError] = useState('');
  const [uploadDocLoading, setUploadDocLoading] = useState(false);
  const [docDeleteConfirm, setDocDeleteConfirm] = useState(null);
  const docFileInputRef = useRef(null);

  const fetchJob = useCallback(async () => {
    try {
      setLoading(true);
      const res = await jobService.getJob(id);
      setJob(res.data);

      const invoicesRes = await invoiceService.getInvoices();
      const allInvoices = invoicesRes.data || [];
      const related = allInvoices.find(inv => String(inv.job_id) === String(id));
      setInvoice(related || null);

      const notesRes = await jobNoteService.getNotes(id);
      setNotes(notesRes.data || []);

      const auditRes = await jobAuditService.getAudits(id);
      setAuditLog(auditRes.data || []);

      const journeysRes = await journeyService.getJourneys();
      setJourneys((journeysRes.data || []).filter(j => String(j.job_id) === String(id)));

      const estimatesRes = await estimateService.getJobEstimates(id);
      setEstimates(estimatesRes.data || []);

      const quotesRes = await quoteService.getJobQuotes(id);
      setQuotes(quotesRes.data || []);

      const expensesRes = await expenseService.getJobExpenses(id);
      setJobExpenses(expensesRes.data?.data || []);

      const tasksRes = await taskService.getJobTasks(id);
      setJobTasks(tasksRes.data?.data || []);

      const docsRes = await documentService.getJobDocuments(id);
      setJobDocuments(docsRes.data || []);

      const setsRes = await taskSetService.getTaskSets();
      setTaskSets(setsRes.data || []);

      setError('');
    } catch (err) {
      setError('Failed to load job.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const calcTotal = (value, vatRate) => {
    const net = parseFloat(value);
    const rate = vatRate ? parseFloat(vatRate.rate) : null;
    if (!rate || isNaN(net)) return null;
    return net * (1 + rate / 100);
  };

  const calcAmountDue = (inv) => {
    const gross = calcTotal(inv.job?.value, inv.job?.vat_rate);
    const total = gross !== null ? gross : parseFloat(inv.job?.value);
    if (isNaN(total)) return null;
    const paid = inv.amount_paid != null ? parseFloat(inv.amount_paid) : 0;
    return Math.max(0, total - paid);
  };

  const handleInvoiceSend = async () => {
    try {
      const res = await invoiceService.sendInvoice(invoice.id);
      await fetchJob();
      showSuccess(res.message || `Invoice #${invoice.invoice_number} sent.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invoice.');
    }
  };

  const openMarkPaid = () => {
    const due = calcAmountDue(invoice);
    setMarkPaidInvoice(invoice);
    setPayForm({ date_paid: new Date().toISOString().slice(0, 10), amount_paid: due !== null ? due.toFixed(2) : '' });
  };

  const handleMarkPaid = async (e) => {
    e.preventDefault();
    const remainingDue = calcAmountDue(markPaidInvoice);
    const newPayment = parseFloat(payForm.amount_paid);
    const previouslyPaid = markPaidInvoice.amount_paid != null ? parseFloat(markPaidInvoice.amount_paid) : 0;
    const totalPaid = previouslyPaid + newPayment;
    const status = remainingDue !== null && newPayment < remainingDue ? 'Partially Paid' : 'Paid';
    setPayLoading(true);
    try {
      await invoiceService.updateInvoice(markPaidInvoice.id, {
        job_id: markPaidInvoice.job_id,
        status,
        date_paid: payForm.date_paid,
        amount_paid: totalPaid,
      });
      await fetchJob();
      showSuccess(`Invoice #${markPaidInvoice.invoice_number} marked as ${status}.`);
      setMarkPaidInvoice(null);
    } catch {
      showSuccess('Failed to update invoice.');
    } finally {
      setPayLoading(false);
    }
  };

  const handleInvoiceEdit = async (formData) => {
    await invoiceService.updateInvoice(editingInvoice.id, formData);
    setEditingInvoice(null);
    await fetchJob();
    showSuccess('Invoice updated successfully!');
  };

  const handleInvoiceDelete = async () => {
    try {
      await invoiceService.deleteInvoice(invoice.id);
      setInvoiceDeleteConfirm(false);
      await fetchJob();
      showSuccess('Invoice deleted successfully!');
    } catch {
      showSuccess('Failed to delete invoice.');
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleMarkComplete = async () => {
    try {
      await jobService.updateJob(id, {
        site_id: job.site_id,
        title: job.title,
        customer_po: job.customer_po,
        description: job.description,
        value: job.value,
        vat_rate_id: job.vat_rate_id,
        status: 'Completed',
      });
      const jobRes = await jobService.getJob(id);
      setJob(jobRes.data);
      const auditRes = await jobAuditService.getAudits(id);
      setAuditLog(auditRes.data || []);
      const completedFormData = {
        site_id: job.site_id,
        title: job.title,
        customer_po: job.customer_po,
        description: job.description,
        value: job.value,
        vat_rate_id: job.vat_rate_id,
        status: 'Completed',
      };
      setCompletedJobDialog({ job, formData: completedFormData });
    } catch {
      showSuccess('Failed to update job status.');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setNoteSubmitting(true);
    try {
      await jobNoteService.addNote(id, noteText.trim());
      setNoteText('');
      setShowAddNoteModal(false);
      const [notesRes, auditRes, jobRes] = await Promise.all([
        jobNoteService.getNotes(id),
        jobAuditService.getAudits(id),
        jobService.getJob(id),
      ]);
      setNotes(notesRes.data || []);
      setAuditLog(auditRes.data || []);
      setJob(jobRes.data);
    } catch {
      showSuccess('Failed to add note.');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await jobNoteService.deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      setNoteDeleteConfirm(null);
      const [auditRes, jobRes] = await Promise.all([
        jobAuditService.getAudits(id),
        jobService.getJob(id),
      ]);
      setAuditLog(auditRes.data || []);
      setJob(jobRes.data);
    } catch {
      showSuccess('Failed to delete note.');
    }
  };

  const handleEditNote = async (noteId) => {
    if (!noteEditText.trim()) return;
    try {
      await jobNoteService.updateNote(noteId, noteEditText.trim());
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, note: noteEditText.trim() } : n));
      setNoteEditId(null);
      setNoteEditText('');
    } catch {
      showSuccess('Failed to update note.');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' at '
      + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const statusBadge = (status) => {
    const styles = {
      'Quoted':      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
      'Completed':   'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      'Cancelled':   'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    };
    const cls = styles[status] || styles['Quoted'];
    return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status || 'Quoted'}</span>;
  };

  const invoiceStatusBadge = (status) => {
    const styles = {
      'Not Sent':      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
      'Sent':          'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      'Paid':          'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      'Partially Paid':'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
      'Overdue':       'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
      'Cancelled':     'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    };
    const cls = styles[status] || styles['Not Sent'];
    return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-red-500">{error || 'Job not found.'}</div>
      </div>
    );
  }

  const total = calcTotal(job.value, job.vat_rate);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <Link to="/jobs" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Jobs</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">{job?.title || '…'}</span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <span className="text-sm text-gray-600 dark:text-gray-300">{user?.name}</span>
              <button
                onClick={logout}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm">
            {successMessage}
          </div>
        )}

        {/* Title + actions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{job.title}</h1>
              {statusBadge(job.status)}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {job.status === 'In Progress' && (
                <button
                  onClick={handleMarkComplete}
                  className="px-4 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                >
                  Mark Complete
                </button>
              )}
              {!invoice && (
                <button
                  onClick={() => setShowInvoiceModal(true)}
                  className="px-4 py-2 text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
                >
                  Generate Invoice
                </button>
              )}
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
              >
                Edit Job
              </button>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-6">
            {/* Customer */}
            {job.site?.customer && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Customer</p>
                <Link to={`/customers/${job.site.customer_id}`} className="text-base font-semibold text-blue-600 dark:text-blue-400 hover:underline">{job.site.customer.name}</Link>
                {[job.site.customer.street_address, job.site.customer.town, job.site.customer.county, job.site.customer.country, job.site.customer.postcode].filter(Boolean).length > 0 && (
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {[job.site.customer.street_address, job.site.customer.town, job.site.customer.county, job.site.customer.country].filter(Boolean).map((line, i) => (
                      <span key={i} className="block">{line}</span>
                    ))}
                    {job.site.customer.postcode && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([job.site.customer.name, job.site.customer.street_address, job.site.customer.town, job.site.customer.county, job.site.customer.postcode].filter(Boolean).join(', '))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:underline text-blue-500 dark:text-blue-400"
                      >{job.site.customer.postcode}</a>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Site */}
            {job.site && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Site</p>
                <Link to={`/sites/${job.site_id}`} className="text-base font-semibold text-blue-600 dark:text-blue-400 hover:underline">{job.site.name}</Link>
                {[job.site.street_address, job.site.town, job.site.county, job.site.postcode].filter(Boolean).length > 0 && (
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {[job.site.street_address, job.site.town, job.site.county].filter(Boolean).map((line, i) => (
                      <span key={i} className="block">{line}</span>
                    ))}
                    {job.site.postcode && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([job.site.name, job.site.street_address, job.site.town, job.site.county, job.site.postcode].filter(Boolean).join(', '))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:underline text-blue-500 dark:text-blue-400"
                      >{job.site.postcode}</a>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Job Details</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {job.description && (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</dt>
                <dd className="mt-1 text-gray-900 dark:text-white whitespace-pre-wrap">{job.description}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer PO</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{job.customer_po || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Gross Value</dt>
              <dd className="mt-1 text-gray-900 dark:text-white font-medium">{formatValue(job.value)}</dd>
            </div>
            {jobExpenses.length > 0 && (
              <div>
                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Adjusted Gross Value</dt>
                <dd className="mt-1 font-medium">
                  {(() => {
                    const expTotal = jobExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
                    const adjusted = parseFloat(job.value || 0) - expTotal;
                    return (
                      <span className={adjusted < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}>
                        {formatValue(adjusted)}
                      </span>
                    );
                  })()}
                </dd>
              </div>
            )}
            {total !== null && (
              <div>
                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total (inc. {parseFloat(job.vat_rate.rate)}% VAT)
                </dt>
                <dd className="mt-1 text-gray-900 dark:text-white font-medium">{formatValue(total)}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{formatDate(job.created_at)}</dd>
            </div>
          </dl>
        </div>

        {/* Finance tabs: Estimates / Quotes / Invoice / Expenses / Journeys */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Tab bar */}
          <div className="flex items-center border-b border-gray-200 dark:border-gray-700 px-4 overflow-x-auto">
            {[
              { key: 'estimates', label: 'Estimates', count: estimates.length },
              { key: 'quotes',    label: 'Quotes',    count: quotes.length },
              { key: 'invoice',   label: 'Invoice',   count: invoice ? 1 : 0 },
              { key: 'expenses',  label: 'Expenses',  count: jobExpenses.length },
              { key: 'journeys',  label: 'Journeys',  count: journeys.length },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveFinanceTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  activeFinanceTab === tab.key
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                    activeFinanceTab === tab.key
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6">

          {/* === ESTIMATES TAB === */}
          {activeFinanceTab === 'estimates' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estimates</span>
                <button onClick={() => setShowAddEstimateModal(true)} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors">+ Add Estimate</button>
              </div>
              {estimates.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No estimates yet.</p>
              ) : (
                <div className="space-y-2">
                  {estimates.map((estimate) => {
                    const total = estimate.items?.reduce((sum, item) => sum + Math.abs(parseFloat(item.value || 0)), 0) || 0;
                    const gross = estimate.items?.reduce((sum, item) => sum + parseFloat(item.value || 0), 0) || 0;
                    return (
                      <div key={estimate.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                        <div className="flex-1 min-w-0">
                          <button onClick={() => window.open(`/estimates/${estimate.id}`, '_blank')} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline text-left">{estimate.title}</button>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{estimate.items?.length || 0} line {estimate.items?.length === 1 ? 'item' : 'items'}{' · '}{formatDate(estimate.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                            {formatValue(total)}
                            {gross !== total && <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">({formatValue(gross)})</span>}
                          </span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setEditingEstimate(estimate)} className="px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-md transition-colors">Edit</button>
                            <button
                              onClick={async () => {
                                try {
                                  await quoteService.createQuote({ job_id: id, title: estimate.title, status: 'Created', items: (estimate.items || []).filter(item => parseFloat(item.value) >= 0).map((item, i) => ({ description: item.description, value: parseFloat(item.value), sort_order: i })) });
                                  const quotesRes = await quoteService.getJobQuotes(id);
                                  setQuotes(quotesRes.data || []);
                                  showSuccess('Converted to quote!');
                                } catch { showSuccess('Failed to convert to quote.'); }
                              }}
                              className="px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-md transition-colors"
                            >Convert to Quote</button>
                            {estimateDeleteConfirm === estimate.id ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                                <button onClick={async () => { await estimateService.deleteEstimate(estimate.id); setEstimates(prev => prev.filter(e => e.id !== estimate.id)); setEstimateDeleteConfirm(null); showSuccess('Estimate deleted.'); }} className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors">Yes</button>
                                <button onClick={() => setEstimateDeleteConfirm(null)} className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">No</button>
                              </div>
                            ) : (
                              <button onClick={() => setEstimateDeleteConfirm(estimate.id)} className="px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-md transition-colors">Delete</button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* === QUOTES TAB === */}
          {activeFinanceTab === 'quotes' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quotes</span>
                <button onClick={() => setShowAddQuoteModal(true)} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors">+ Add Quote</button>
              </div>
              {quotes.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No quotes yet.</p>
              ) : (
                <div className="space-y-2">
                  {quotes.map((quote) => {
                    const quoteStatusBadge = (s) => {
                      const styles = { 'Created': 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300', 'Waiting on customer': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', 'Accepted': 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', 'Rejected': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' };
                      return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[s] || styles['Created']}`}>{s}</span>;
                    };
                    const qTotal = quote.items?.reduce((sum, item) => sum + Math.abs(parseFloat(item.value || 0)), 0) || 0;
                    const qGross = quote.items?.reduce((sum, item) => sum + parseFloat(item.value || 0), 0) || 0;
                    return (
                      <div key={quote.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{quote.title}</span>
                            {quote.quote_number && <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">#{quote.quote_number}</span>}
                            {quoteStatusBadge(quote.status)}
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{quote.items?.length || 0} line {quote.items?.length === 1 ? 'item' : 'items'}{' · '}{formatDate(quote.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                            {formatValue(qTotal)}
                            {qGross !== qTotal && <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">({formatValue(qGross)})</span>}
                          </span>
                          <button onClick={() => window.open(`/quotes/${quote.id}/print`, '_blank')} className="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors">Print</button>
                          <button onClick={() => setEditingQuote(quote)} className="px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-md transition-colors">Edit</button>
                          {quoteDeleteConfirm === quote.id ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                              <button onClick={async () => { await quoteService.deleteQuote(quote.id); setQuotes(prev => prev.filter(q => q.id !== quote.id)); setQuoteDeleteConfirm(null); showSuccess('Quote deleted.'); }} className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors">Yes</button>
                              <button onClick={() => setQuoteDeleteConfirm(null)} className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">No</button>
                            </div>
                          ) : (
                            <button onClick={() => setQuoteDeleteConfirm(quote.id)} className="px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-md transition-colors">Delete</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* === INVOICE TAB === */}
          {activeFinanceTab === 'invoice' && (
            <>
              {!invoice ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <p className="text-sm text-gray-400 dark:text-gray-500">No invoice yet.</p>
                  <button
                    onClick={() => setShowInvoiceModal(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-lg transition-colors"
                  >
                    Create Invoice
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <button onClick={() => window.open(`/invoices/${invoice.id}/print`, '_blank')} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">#{invoice.invoice_number}</button>
                        {invoiceStatusBadge(invoice.status)}
                      </div>
                      <dl className="grid grid-cols-1 gap-y-1 text-sm">
                        {calcTotal(invoice.job?.value, invoice.job?.vat_rate) !== null ? (
                          <div><dt className="inline text-gray-500 dark:text-gray-400">Total (inc. VAT): </dt><dd className="inline font-medium text-gray-900 dark:text-white">{formatValue(calcTotal(invoice.job?.value, invoice.job?.vat_rate))}</dd></div>
                        ) : invoice.job?.value ? (
                          <div><dt className="inline text-gray-500 dark:text-gray-400">Amount: </dt><dd className="inline font-medium text-gray-900 dark:text-white">{formatValue(invoice.job.value)}</dd></div>
                        ) : null}
                        {calcAmountDue(invoice) !== null && calcAmountDue(invoice) > 0 && (
                          <div><dt className="inline text-gray-500 dark:text-gray-400">Amount due: </dt><dd className="inline font-medium text-gray-900 dark:text-white">{formatValue(calcAmountDue(invoice))}</dd></div>
                        )}
                        {invoice.date_paid && <div><dt className="inline text-gray-500 dark:text-gray-400">Paid: </dt><dd className="inline text-gray-900 dark:text-white">{formatDate(invoice.date_paid)}</dd></div>}
                        {invoice.amount_paid && <div><dt className="inline text-gray-500 dark:text-gray-400">Amount paid: </dt><dd className="inline text-gray-900 dark:text-white">{formatValue(invoice.amount_paid)}</dd></div>}
                        {invoice.due_date && <div><dt className="inline text-gray-500 dark:text-gray-400">Due: </dt><dd className="inline text-gray-900 dark:text-white">{formatDate(invoice.due_date)}</dd></div>}
                      </dl>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <button onClick={() => window.open(`/invoices/${invoice.id}/print`, '_blank')} className="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30 hover:bg-gray-200 dark:hover:bg-gray-900/50 rounded-md transition-colors">Print</button>
                      {invoice.status !== 'Sent' && invoice.status !== 'Paid' && <button onClick={handleInvoiceSend} className="px-2.5 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 rounded-md transition-colors">Send</button>}
                      {invoice.status !== 'Paid' && invoice.status !== 'Cancelled' && <button onClick={openMarkPaid} className="px-2.5 py-1 text-xs font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-md transition-colors">Mark as Paid</button>}
                      <button onClick={() => setEditingInvoice(invoice)} className="px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-md transition-colors">Edit</button>
                      <button onClick={() => setInvoiceDeleteConfirm(true)} className="px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-md transition-colors">Delete</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* === EXPENSES TAB === */}
          {activeFinanceTab === 'expenses' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Expenses</span>
                <button onClick={() => { setEditingExpense(null); setShowAddExpenseModal(true); }} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors">+ Add Expense</button>
              </div>
              {jobExpenses.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No expenses recorded for this job.</p>
              ) : (
                <div className="space-y-2">
                  {jobExpenses.map(expense => (
                    <div key={expense.id} className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{expense.reason?.name || '—'}</span>
                        {expense.description && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 truncate">{expense.description}</span>}
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{expense.expense_date ? new Date(expense.expense_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">£{parseFloat(expense.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <button onClick={() => { setEditingExpense(expense); setShowAddExpenseModal(true); }} className="px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-md transition-colors">Edit</button>
                        {expenseDeleteConfirm === expense.id ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                            <button onClick={async () => { try { await expenseService.deleteExpense(expense.id); const res = await expenseService.getJobExpenses(id); setJobExpenses(res.data?.data || []); setExpenseDeleteConfirm(null); showSuccess('Expense deleted.'); } catch { setError('Failed to delete expense.'); } }} className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors">Yes</button>
                            <button onClick={() => setExpenseDeleteConfirm(null)} className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setExpenseDeleteConfirm(expense.id)} className="px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-md transition-colors">Delete</button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">Total: £{jobExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* === JOURNEYS TAB === */}
          {activeFinanceTab === 'journeys' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Journeys</span>
                  {journeys.length > 0 && <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{journeys.reduce((sum, j) => sum + parseFloat(j.mileage || 0), 0).toFixed(1)} mi total</span>}
                </div>
                <button onClick={() => setShowAddJourneyModal(true)} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors">+ Add Journey</button>
              </div>
              {journeys.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No journeys yet.</p>
              ) : (
                <div className="space-y-0 max-h-80 overflow-y-auto pr-1">
                  {[...journeys].sort((a, b) => new Date(b.started_at) - new Date(a.started_at)).map(journey => (
                    <div key={journey.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{journey.reason?.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(journey.start_postcode)}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{journey.start_postcode}</a>
                            {' → '}
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(journey.end_postcode)}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{journey.end_postcode}</a>
                          </span>
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{parseFloat(journey.mileage).toFixed(1)} mi</span>
                        </div>
                        {journey.started_at && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDateTime(journey.started_at)}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(journey.start_postcode)}&destination=${encodeURIComponent(journey.end_postcode)}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-md transition-colors">Maps</a>
                        <button onClick={() => setEditingJourney(journey)} className="px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-md transition-colors">Edit</button>
                        {journeyDeleteConfirm === journey.id ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                            <button onClick={async () => { await journeyService.deleteJourney(journey.id); setJourneys(prev => prev.filter(j => j.id !== journey.id)); setJourneyDeleteConfirm(null); const auditRes = await jobAuditService.getAudits(id); setAuditLog(auditRes.data || []); showSuccess('Journey deleted.'); }} className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors">Yes</button>
                            <button onClick={() => setJourneyDeleteConfirm(null)} className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setJourneyDeleteConfirm(journey.id)} className="px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-md transition-colors">Delete</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          </div>
        </div>

        {/* Notes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notes</h2>
            <button
              onClick={() => { setNoteText(''); setShowAddNoteModal(true); }}
              className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
            >+ Add Note</button>
          </div>

          {/* Notes list */}
          {notes.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No notes yet.</p>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {notes.map((note) => (
                <div key={note.id} className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  {noteEditId === note.id ? (
                    <div>
                      <textarea
                        value={noteEditText}
                        onChange={(e) => setNoteEditText(e.target.value.slice(0, 1000))}
                        rows={3}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400 dark:text-gray-500">{noteEditText.length}/1000</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setNoteEditId(null); setNoteEditText(''); }}
                            className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          >Cancel</button>
                          <button
                            onClick={() => handleEditNote(note.id)}
                            disabled={!noteEditText.trim()}
                            className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md transition-colors"
                          >Save</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{note.note}</p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      <span className="font-medium text-gray-500 dark:text-gray-400">{note.user?.name || 'Unknown'}</span>
                      <span className="mx-1.5">·</span>
                      <span>{formatDateTime(note.created_at)}</span>
                    </div>
                    {noteDeleteConfirm === note.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
                        >Yes</button>
                        <button
                          onClick={() => setNoteDeleteConfirm(null)}
                          className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        >No</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        {note.user?.id === user?.id && (
                          <button
                            onClick={() => { setNoteEditId(note.id); setNoteEditText(note.note); setNoteDeleteConfirm(null); }}
                            className="text-xs text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-200 transition-colors"
                          >Edit</button>
                        )}
                        <button
                          onClick={() => setNoteDeleteConfirm(note.id)}
                          className="text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200 transition-colors"
                        >Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>        

        {/* Tasks */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tasks</h2>
              {jobTasks.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {jobTasks.filter(t => t.completed).length}/{jobTasks.length} done
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {selectedTaskIds.length > 0 && (
                <>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{selectedTaskIds.length} selected</span>
                  {selectedTaskIds.some(id => !jobTasks.find(t => t.id === id)?.completed) && (
                    taskCompleteConfirmPending ? (
                    <>
                      <span className="text-xs text-green-600 dark:text-green-400">Mark {selectedTaskIds.length} task{selectedTaskIds.length !== 1 ? 's' : ''} complete?</span>
                      <button
                        disabled={taskBulkLoading}
                        onClick={async () => {
                          setTaskBulkLoading(true);
                          try {
                            await Promise.all(selectedTaskIds.map(tid => taskService.updateTask(tid, { completed: true })));
                            setJobTasks(prev => prev.map(t => selectedTaskIds.includes(t.id) ? { ...t, completed: true } : t));
                            setSelectedTaskIds([]);
                            setTaskCompleteConfirmPending(false);
                            setTaskDeleteConfirmPending(false);
                          } catch { /* ignore */ } finally {
                            setTaskBulkLoading(false);
                          }
                        }}
                        className="text-xs font-semibold text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 disabled:opacity-50 transition-colors"
                      >Yes</button>
                      <button
                        onClick={() => setTaskCompleteConfirmPending(false)}
                        className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                      >No</button>
                    </>
                  ) : (
                    <button
                      disabled={taskBulkLoading}
                      onClick={() => setTaskCompleteConfirmPending(true)}
                      className="text-xs font-semibold text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 disabled:opacity-50 transition-colors"
                    >Mark Complete</button>
                  )
                  )}
                  {taskDeleteConfirmPending ? (
                    <>
                      <span className="text-xs text-red-600 dark:text-red-400">Delete {selectedTaskIds.length} task{selectedTaskIds.length !== 1 ? 's' : ''}?</span>
                      <button
                        disabled={taskBulkLoading}
                        onClick={async () => {
                          setTaskBulkLoading(true);
                          try {
                            await Promise.all(selectedTaskIds.map(tid => taskService.deleteTask(tid)));
                            setJobTasks(prev => prev.filter(t => !selectedTaskIds.includes(t.id)));
                            setSelectedTaskIds([]);
                            setTaskCompleteConfirmPending(false);
                            setTaskDeleteConfirmPending(false);
                          } catch { /* ignore */ } finally {
                            setTaskBulkLoading(false);
                          }
                        }}
                        className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 disabled:opacity-50 transition-colors"
                      >Yes</button>
                      <button
                        onClick={() => setTaskDeleteConfirmPending(false)}
                        className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                      >No</button>
                    </>
                  ) : (
                    <button
                      disabled={taskBulkLoading}
                      onClick={() => setTaskDeleteConfirmPending(true)}
                      className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 disabled:opacity-50 transition-colors"
                    >Delete</button>
                  )}
                </>
              )}
              <button
                onClick={() => { setSelectedTaskSetId(''); setShowApplyTaskSetModal(true); }}
                className="text-xs font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 transition-colors"
              >Apply Set</button>
              <button
                onClick={() => { setNewTaskTitle(''); setShowAddTaskModal(true); }}
                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
              >+ Add Task</button>
            </div>
          </div>
          {jobTasks.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No tasks for this job.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
              {(() => {
                const incompleteTasks = jobTasks.filter(t => !t.completed);
                const allIncompleteSelected = incompleteTasks.length > 0 && incompleteTasks.every(t => selectedTaskIds.includes(t.id));
                const allCompleted = incompleteTasks.length === 0;
                const allCompletedSelected = allCompleted && jobTasks.every(t => selectedTaskIds.includes(t.id));
                if (!allCompleted) {
                  return incompleteTasks.length > 0 ? (
                    <div className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700 mb-1">
                      <input
                        type="checkbox"
                        checked={allIncompleteSelected}
                        onChange={() => {
                          if (allIncompleteSelected) {
                            setSelectedTaskIds(prev => prev.filter(id => !incompleteTasks.some(t => t.id === id)));
                          } else {
                            setSelectedTaskIds(prev => [...new Set([...prev, ...incompleteTasks.map(t => t.id)])]);
                          }
                          setTaskCompleteConfirmPending(false);
                          setTaskDeleteConfirmPending(false);
                        }}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Select all incomplete</span>
                    </div>
                  ) : null;
                }
                return (
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700 mb-1">
                    <input
                      type="checkbox"
                      checked={allCompletedSelected}
                      onChange={() => {
                        if (allCompletedSelected) {
                          setSelectedTaskIds([]);
                        } else {
                          setSelectedTaskIds(jobTasks.map(t => t.id));
                        }
                        setTaskDeleteConfirmPending(false);
                      }}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">Select all completed</span>
                  </div>
                );
              })()}
              {jobTasks.map((task) => (
                <div key={task.id} className="flex items-center gap-3 py-2 group">
                  <input
                    type="checkbox"
                    checked={selectedTaskIds.includes(task.id)}
                    onChange={() => {
                      setSelectedTaskIds(prev =>
                        prev.includes(task.id) ? prev.filter(id => id !== task.id) : [...prev, task.id]
                      );
                    }}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                  />
                  <span className={`flex-1 text-sm ${
                    task.completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'
                  }`}>{task.title}</span>
                  {task.completed && (
                    <button
                      onClick={async () => {
                        setJobTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: false } : t));
                        try {
                          await taskService.updateTask(task.id, { completed: false });
                        } catch {
                          setJobTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: true } : t));
                        }
                      }}
                      className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    >Mark Not Completed</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Documents</h2>
            <button
              onClick={() => { setUploadDocFiles([]); setUploadDocError(''); setShowUploadDocModal(true); }}
              className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
            >↑ Upload</button>
          </div>
          {jobDocuments.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No documents attached to this job.</p>
          ) : (
            <div className="space-y-1">
              {jobDocuments.map((doc) => {
                const FILE_ICONS = {
                  'application/pdf': '📄',
                  'application/msword': '📝',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
                  'application/vnd.ms-excel': '📊',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
                  'text/csv': '📊',
                  'text/plain': '📃',
                  'image/jpeg': '🖼️',
                  'image/jpg': '🖼️',
                  'image/png': '🖼️',
                  'image/gif': '🖼️',
                };
                const icon = FILE_ICONS[doc.mime_type] || '📎';
                const sizeStr = doc.size < 1024 * 1024
                  ? `${(doc.size / 1024).toFixed(1)} KB`
                  : `${(doc.size / (1024 * 1024)).toFixed(1)} MB`;
                return (
                  <div key={doc.id} className="flex items-center gap-3 py-2 group">
                    <span className="text-lg shrink-0">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.original_name}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{sizeStr}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={async () => { try { await documentService.downloadDocument(doc.id, doc.original_name); } catch { showSuccess('Download failed.'); } }}
                        className="px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-md transition-colors"
                      >Download</button>
                      {docDeleteConfirm === doc.id ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                          <button
                            onClick={async () => { try { await documentService.deleteDocument(doc.id); setJobDocuments(prev => prev.filter(d => d.id !== doc.id)); setDocDeleteConfirm(null); showSuccess('Document deleted.'); } catch { showSuccess('Failed to delete.'); } }}
                            className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
                          >Yes</button>
                          <button onClick={() => setDocDeleteConfirm(null)} className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">No</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDocDeleteConfirm(doc.id)}
                          className="px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                        >Delete</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Audit Log */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Audit Log</h2>
          {auditLog.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No audit entries yet.</p>
          ) : (
            <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2 space-y-4 max-h-80 overflow-y-auto pr-2">
              {auditLog.map(entry => (
                <li key={entry.id} className="ml-4">
                  <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-blue-400 dark:bg-blue-500 border-2 border-white dark:border-gray-800" />
                  <p className="text-sm text-gray-800 dark:text-gray-200">{entry.message}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {formatDateTime(entry.created_at)}{entry.user?.name ? ` · ${entry.user.name}` : ''}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </main>

      {editingInvoice && (
        <InvoiceModal
          invoice={editingInvoice}
          onSave={handleInvoiceEdit}
          onClose={() => setEditingInvoice(null)}
        />
      )}

      {invoiceDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Invoice</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete invoice <strong>#{invoice.invoice_number}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setInvoiceDeleteConfirm(false)} className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
              <button onClick={handleInvoiceDelete} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}

      {markPaidInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Mark as Paid</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Invoice #{markPaidInvoice.invoice_number}</p>
            {calcAmountDue(markPaidInvoice) !== null && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Remaining balance (inc. VAT): <strong>{formatValue(calcAmountDue(markPaidInvoice))}</strong>
              </p>
            )}
            <form onSubmit={handleMarkPaid} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Date</label>
                <input
                  type="date"
                  required
                  max={new Date().toISOString().slice(0, 10)}
                  value={payForm.date_paid}
                  onChange={e => setPayForm(p => ({ ...p, date_paid: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount Paid inc. VAT (£)</label>
                <input
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={payForm.amount_paid}
                  onChange={e => setPayForm(p => ({ ...p, amount_paid: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {payForm.amount_paid && calcAmountDue(markPaidInvoice) !== null && (
                  <p className="text-xs mt-1 font-medium">
                    {parseFloat(payForm.amount_paid) < calcAmountDue(markPaidInvoice)
                      ? <span className="text-amber-600 dark:text-amber-400">Will be marked as Partially Paid</span>
                      : <span className="text-green-600 dark:text-green-400">Will be marked as Paid</span>}
                  </p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setMarkPaidInvoice(null)} className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
                <button type="submit" disabled={payLoading} className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50 transition-colors">{payLoading ? 'Saving...' : 'Save Payment'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {completedJobDialog && (
        <JobCompletedDialog
          job={completedJobDialog.job}
          formData={completedJobDialog.formData}
          onClose={(msg) => {
            setCompletedJobDialog(null);
            fetchJob();
            if (msg) showSuccess(msg);
          }}
        />
      )}

      {showEditModal && (
        <JobModal
          job={job}
          onSave={async (formData) => {
            const prevStatus = job.status;
            await jobService.updateJob(job.id, formData);
            setShowEditModal(false);
            await fetchJob();
            if (prevStatus !== 'Completed' && formData.status === 'Completed') {
              setCompletedJobDialog({ job, formData });
            } else {
              showSuccess('Job updated successfully!');
            }
          }}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showInvoiceModal && (
        <InvoiceModal
          defaultJobId={job.id}
          onSave={async (formData) => {
            await invoiceService.createInvoice(formData);
            setShowInvoiceModal(false);
            await fetchJob();
            showSuccess('Invoice created successfully!');
          }}
          onClose={() => setShowInvoiceModal(false)}
        />
      )}

      {showAddJourneyModal && (
        <JourneyModal
          prefill={{ job_id: id }}
          onSave={async (formData) => {
            await journeyService.createJourney(formData);
            setShowAddJourneyModal(false);
            const journeysRes = await journeyService.getJourneys();
            setJourneys((journeysRes.data || []).filter(j => String(j.job_id) === String(id)));
            const auditRes = await jobAuditService.getAudits(id);
            setAuditLog(auditRes.data || []);
            showSuccess('Journey added!');
          }}
          onClose={() => setShowAddJourneyModal(false)}
        />
      )}

      {editingJourney && (
        <JourneyModal
          journey={editingJourney}
          onSave={async (formData) => {
            await journeyService.updateJourney(editingJourney.id, formData);
            setEditingJourney(null);
            const journeysRes = await journeyService.getJourneys();
            setJourneys((journeysRes.data || []).filter(j => String(j.job_id) === String(id)));
            const auditRes = await jobAuditService.getAudits(id);
            setAuditLog(auditRes.data || []);
            showSuccess('Journey updated!');
          }}
          onClose={() => setEditingJourney(null)}
        />
      )}

      {showAddEstimateModal && (
        <EstimateModal
          jobId={id}
          defaultTitle={`${job?.title || ''}${job?.site?.name ? ` @ ${job.site.name}` : ''}`}
          onClose={() => setShowAddEstimateModal(false)}
          onSaved={async () => {
            setShowAddEstimateModal(false);
            const estimatesRes = await estimateService.getJobEstimates(id);
            setEstimates(estimatesRes.data || []);
            showSuccess('Estimate created!');
          }}
        />
      )}

      {editingEstimate && (
        <EstimateModal
          jobId={id}
          estimate={editingEstimate}
          onClose={() => setEditingEstimate(null)}
          onSaved={async () => {
            setEditingEstimate(null);
            const estimatesRes = await estimateService.getJobEstimates(id);
            setEstimates(estimatesRes.data || []);
            showSuccess('Estimate updated!');
          }}
        />
      )}

      {showAddQuoteModal && (
        <QuoteModal
          jobId={id}
          defaultTitle={`${job?.title || ''}${job?.site?.name ? ` @ ${job.site.name}` : ''}`}
          onClose={() => setShowAddQuoteModal(false)}
          onSaved={async () => {
            setShowAddQuoteModal(false);
            const quotesRes = await quoteService.getJobQuotes(id);
            setQuotes(quotesRes.data || []);
            showSuccess('Quote created!');
          }}
        />
      )}

      {editingQuote && (
        <QuoteModal
          jobId={id}
          quote={editingQuote}
          onClose={() => setEditingQuote(null)}
          onSaved={async () => {
            setEditingQuote(null);
            const quotesRes = await quoteService.getJobQuotes(id);
            setQuotes(quotesRes.data || []);
            showSuccess('Quote updated!');
          }}
        />
      )}

      {showAddNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddNoteModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-lg p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">New Note</h2>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value.slice(0, 1000))}
              rows={5}
              autoFocus
              placeholder="Write your note..."
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <div className="flex items-center justify-between mt-2 mb-4">
              <span className="text-xs text-gray-400 dark:text-gray-500">{noteText.length}/1000</span>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAddNoteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >Cancel</button>
              <button
                onClick={handleAddNote}
                disabled={!noteText.trim() || noteSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >{noteSubmitting ? 'Adding...' : 'Add Note'}</button>
            </div>
          </div>
        </div>
      )}

      {showApplyTaskSetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowApplyTaskSetModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-lg p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Apply Task Set</h2>
            {taskSets.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No task sets found. Create some on the Task Sets page.</p>
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select a task set</label>
                <select
                  value={selectedTaskSetId}
                  onChange={(e) => setSelectedTaskSetId(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                >
                  <option value="">-- Choose a task set --</option>
                  {taskSets.map((ts) => (
                    <option key={ts.id} value={ts.id}>{ts.name} ({ts.items?.length ?? 0} tasks)</option>
                  ))}
                </select>
                {selectedTaskSetId && (() => {
                  const selected = taskSets.find(ts => String(ts.id) === String(selectedTaskSetId));
                  return selected && selected.items?.length > 0 ? (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Tasks to add:</p>
                      <ul className="space-y-1">
                        {selected.items.map((item) => (
                          <li key={item.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                            {item.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null;
                })()}
              </>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowApplyTaskSetModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >Cancel</button>
              {taskSets.length > 0 && (
                <button
                  onClick={async () => {
                    if (!selectedTaskSetId || applyingTaskSet) return;
                    const selected = taskSets.find(ts => String(ts.id) === String(selectedTaskSetId));
                    if (!selected || !selected.items?.length) return;
                    setApplyingTaskSet(true);
                    try {
                      for (const item of selected.items) {
                        await taskService.createTask({ job_id: id, title: item.title });
                      }
                      const res = await taskService.getJobTasks(id);
                      setJobTasks(res.data?.data || []);
                      setShowApplyTaskSetModal(false);
                      showSuccess(`Added ${selected.items.length} task${selected.items.length !== 1 ? 's' : ''} from "${selected.name}".`);
                    } catch { /* ignore */ } finally {
                      setApplyingTaskSet(false);
                    }
                  }}
                  disabled={!selectedTaskSetId || applyingTaskSet}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >{applyingTaskSet ? 'Applying...' : 'Apply'}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddTaskModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-lg p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">New Task</h2>
            <input
              type="text"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value.slice(0, 500))}
              autoFocus
              placeholder="Task description..."
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTaskTitle.trim() && !taskSubmitting) {
                  e.preventDefault();
                  (async () => {
                    setTaskSubmitting(true);
                    try {
                      await taskService.createTask({ job_id: id, title: newTaskTitle.trim() });
                      const res = await taskService.getJobTasks(id);
                      setJobTasks(res.data?.data || []);
                      setNewTaskTitle('');
                      setShowAddTaskModal(false);
                    } catch { /* ignore */ } finally {
                      setTaskSubmitting(false);
                    }
                  })();
                }
              }}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowAddTaskModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >Cancel</button>
              <button
                onClick={async () => {
                  if (!newTaskTitle.trim() || taskSubmitting) return;
                  setTaskSubmitting(true);
                  try {
                    await taskService.createTask({ job_id: id, title: newTaskTitle.trim() });
                    const res = await taskService.getJobTasks(id);
                    setJobTasks(res.data?.data || []);
                    setNewTaskTitle('');
                    setShowAddTaskModal(false);
                  } catch { /* ignore */ } finally {
                    setTaskSubmitting(false);
                  }
                }}
                disabled={!newTaskTitle.trim() || taskSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >{taskSubmitting ? 'Adding...' : 'Add Task'}</button>
            </div>
          </div>
        </div>
      )}

      {showAddExpenseModal && (
        <ExpenseModal
          expense={editingExpense}
          defaultJobId={id}
          onClose={() => { setShowAddExpenseModal(false); setEditingExpense(null); }}
          onSaved={async () => {
            setShowAddExpenseModal(false);
            setEditingExpense(null);
            const res = await expenseService.getJobExpenses(id);
            setJobExpenses(res.data?.data || []);
            showSuccess(editingExpense ? 'Expense updated!' : 'Expense added!');
          }}
        />
      )}

      {/* Upload Document Modal */}
      {showUploadDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Upload Document</h3>
            {uploadDocError && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
                {uploadDocError}
              </div>
            )}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!uploadDocFiles.length) return;
                setUploadDocLoading(true);
                setUploadDocError('');
                try {
                  for (const file of uploadDocFiles) {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('job_id', id);
                    await documentService.uploadDocument(formData);
                  }
                  const docsRes = await documentService.getJobDocuments(id);
                  setJobDocuments(docsRes.data || []);
                  setShowUploadDocModal(false);
                  setUploadDocFiles([]);
                  if (docFileInputRef.current) docFileInputRef.current.value = '';
                  showSuccess(`${uploadDocFiles.length} document${uploadDocFiles.length !== 1 ? 's' : ''} uploaded successfully!`);
                } catch (err) {
                  const errors = err.response?.data?.errors;
                  setUploadDocError(errors?.file?.[0] || err.response?.data?.message || 'Upload failed.');
                } finally {
                  setUploadDocLoading(false);
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  ref={docFileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  multiple
                  required
                  onChange={(e) => setUploadDocFiles(Array.from(e.target.files || []))}
                  className="w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50 cursor-pointer"
                />
                {uploadDocFiles.length > 1 && (
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">{uploadDocFiles.length} files selected</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Accepted: images, PDF, Word, Excel, CSV, TXT. Max 20 MB each.</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowUploadDocModal(false); setUploadDocFiles([]); setUploadDocError(''); if (docFileInputRef.current) docFileInputRef.current.value = ''; }}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >Cancel</button>
                <button
                  type="submit"
                  disabled={uploadDocLoading || !uploadDocFiles.length}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >{uploadDocLoading ? 'Uploading…' : uploadDocFiles.length > 1 ? `Upload ${uploadDocFiles.length} Files` : 'Upload'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ViewJob;
